import { z } from 'zod';

/**
 * ÜRÜNÜN KONUŞTUĞU DİLLER — tek kaynak (29 Eyl 2026).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 DİL LİSTESİ YALNIZ BURADA
 * ═══════════════════════════════════════════════════════════════════════════
 * Web'in rota dilleri (`apps/web/src/i18n/routing.ts`), API'nin kabul ettiği
 * `User.locale` değerleri, yasal metin / blog / changelog sekmeleri, bildirim
 * yedek kitapları ve çeviri betiği bu listeyi okuyor. Eskiden `['en', 'tr']`
 * kırk ayrı dosyada elle yazılıydı ve üçüncü bir dil, hangisinin unutulduğu
 * ancak o dilde bir sayfa 404 verdiğinde anlaşılan bir av demekti.
 *
 * Sıra ÖNEMLİ ve kullanıcının verdiği sıra: dil menüsü, yönetici panelindeki
 * sekmeler ve çeviri betiğinin raporu bu sırayla basılıyor.
 *
 * ---------------------------------------------------------------------------
 * El yazımı ↔ makine çevirisi
 * ---------------------------------------------------------------------------
 * `en` ve `tr` İNSAN yazımı: `messages/{en,tr}.json`, kod içindeki
 * `{ tr, en }` nesneleri ve bildirim kitapları elle yazılıyor. Diğer on bir
 * dil DeepL ile İngilizceden çevriliyor (`scripts/i18n/translate.mjs`) ve
 * çevirisi eksik kalan her metin İngilizceye düşüyor — ekranda ham anahtar
 * ya da boşluk görünmüyor.
 *
 * Çeviri betiği `tr`ye DOKUNMAZ: Türkçe metin İngilizcenin çevirisi değil,
 * kendi başına yazılmış bir metin.
 */
export const LOCALES = [
  'en',
  'tr',
  'de',
  'fr',
  'es',
  'it',
  'pt',
  'ru',
  'nl',
  'ar',
  'ko',
  'zh',
  'ja',
] as const;

export type Locale = (typeof LOCALES)[number];

export const localeSchema = z.enum(LOCALES);

/** Eşleşme bulunamadığında. Ürünün kaynak dili İngilizce. */
export const DEFAULT_LOCALE: Locale = 'en';

/** Metni insan tarafından yazılan diller — çeviri betiğinin kaynağı ya da dokunmadığı. */
export const AUTHORED_LOCALES: readonly Locale[] = ['en', 'tr'];

/** Metni DeepL ile İngilizceden üretilen diller. */
export const TRANSLATED_LOCALES: readonly Locale[] = LOCALES.filter(
  (code) => !AUTHORED_LOCALES.includes(code),
);

export interface LocaleMeta {
  code: Locale;
  /** Dil menüsünde görünen ad — dilin KENDİ adıyla ("Deutsch", "日本語"). */
  nativeName: string;
  /**
   * İngilizce ad. Dil modeline giden talimatlarda kullanılıyor ("Reply in
   * German"): modelin talimat dili İngilizce ve dil adını da o dilde okuyor.
   */
  englishName: string;
  dir: 'ltr' | 'rtl';
  /**
   * `Intl.*` için BCP 47 etiketi.
   *
   * ⚠️ Arapça bilerek bölgesiz: `ar-SA` varsayılan takvimi Hicri
   * (islamic-umalqura) yapıyor ve panelin her tarihi farklı bir yıl
   * gösterirdi. `ar` Miladi takvimle biçimliyor.
   */
  intl: string;
  /** Open Graph `og:locale` biçimi. */
  ogLocale: string;
  /**
   * DeepL hedef dil kodu. `null` = el yazımı dil, çevrilmiyor.
   *
   * Portekizce Brezilya (`PT-BR`), Çince Basitleştirilmiş (`ZH-HANS`):
   * `pt` ve `zh` rota kodları tek varyant taşıyor, ikisi de en geniş kitle.
   */
  deepl: string | null;
}

export const LOCALE_META: Record<Locale, LocaleMeta> = {
  en: {
    code: 'en',
    nativeName: 'English',
    englishName: 'English',
    dir: 'ltr',
    intl: 'en-US',
    ogLocale: 'en_US',
    deepl: null,
  },
  tr: {
    code: 'tr',
    nativeName: 'Türkçe',
    englishName: 'Turkish',
    dir: 'ltr',
    intl: 'tr-TR',
    ogLocale: 'tr_TR',
    deepl: null,
  },
  de: {
    code: 'de',
    nativeName: 'Deutsch',
    englishName: 'German',
    dir: 'ltr',
    intl: 'de-DE',
    ogLocale: 'de_DE',
    deepl: 'DE',
  },
  fr: {
    code: 'fr',
    nativeName: 'Français',
    englishName: 'French',
    dir: 'ltr',
    intl: 'fr-FR',
    ogLocale: 'fr_FR',
    deepl: 'FR',
  },
  es: {
    code: 'es',
    nativeName: 'Español',
    englishName: 'Spanish',
    dir: 'ltr',
    intl: 'es-ES',
    ogLocale: 'es_ES',
    deepl: 'ES',
  },
  it: {
    code: 'it',
    nativeName: 'Italiano',
    englishName: 'Italian',
    dir: 'ltr',
    intl: 'it-IT',
    ogLocale: 'it_IT',
    deepl: 'IT',
  },
  pt: {
    code: 'pt',
    nativeName: 'Português',
    englishName: 'Portuguese',
    dir: 'ltr',
    intl: 'pt-BR',
    ogLocale: 'pt_BR',
    deepl: 'PT-BR',
  },
  ru: {
    code: 'ru',
    nativeName: 'Русский',
    englishName: 'Russian',
    dir: 'ltr',
    intl: 'ru-RU',
    ogLocale: 'ru_RU',
    deepl: 'RU',
  },
  nl: {
    code: 'nl',
    nativeName: 'Nederlands',
    englishName: 'Dutch',
    dir: 'ltr',
    intl: 'nl-NL',
    ogLocale: 'nl_NL',
    deepl: 'NL',
  },
  ar: {
    code: 'ar',
    nativeName: 'العربية',
    englishName: 'Arabic',
    dir: 'rtl',
    intl: 'ar',
    ogLocale: 'ar_AR',
    deepl: 'AR',
  },
  ko: {
    code: 'ko',
    nativeName: '한국어',
    englishName: 'Korean',
    dir: 'ltr',
    intl: 'ko-KR',
    ogLocale: 'ko_KR',
    deepl: 'KO',
  },
  zh: {
    code: 'zh',
    nativeName: '中文',
    englishName: 'Simplified Chinese',
    dir: 'ltr',
    intl: 'zh-CN',
    ogLocale: 'zh_CN',
    deepl: 'ZH-HANS',
  },
  ja: {
    code: 'ja',
    nativeName: '日本語',
    englishName: 'Japanese',
    dir: 'ltr',
    intl: 'ja-JP',
    ogLocale: 'ja_JP',
    deepl: 'JA',
  },
};

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

/**
 * Serbest bir dil değerini rota diline indirger.
 *
 * `User.locale` serbest dize (şema yorumu), Google profili `pt-BR` gönderiyor,
 * eski satırlarda `TR` yazıyor olabilir. Hepsi aynı kurala gidiyor: ilk alt
 * etiket, küçük harf, bilinmiyorsa `fallback`.
 */
export function toLocale(value: string | null | undefined, fallback: Locale = DEFAULT_LOCALE): Locale {
  const primary = (value ?? '').trim().toLowerCase().split(/[-_]/)[0] ?? '';
  return isLocale(primary) ? primary : fallback;
}

export function localeDir(locale: string): 'ltr' | 'rtl' {
  return isLocale(locale) ? LOCALE_META[locale].dir : 'ltr';
}

export function isRtlLocale(locale: string): boolean {
  return localeDir(locale) === 'rtl';
}

/** Dil modeli talimatları için İngilizce dil adı ("Reply in German"). */
export function languageName(locale: string): string {
  return LOCALE_META[toLocale(locale)].englishName;
}

/** `Intl.*` için etiket. Bilinmeyen dil İngilizce biçimlenir. */
export function intlLocale(locale: string): string {
  return LOCALE_META[toLocale(locale)].intl;
}

/**
 * `Accept-Language` başlığından ürünün dillerinden birini seçer.
 *
 * `q` değerine göre sıralanıyor — tarayıcılar listeyi çoğunlukla azalan
 * sırada gönderiyor ama elle yazılan başlıklar göndermiyor. Bölge etiketi
 * düşürülüyor (`de-CH` → `de`, `zh-TW` → `zh`, `pt-PT` → `pt`).
 *
 * Hiçbiri eşleşmezse `null` — varsayılana düşme kararı çağıranın.
 */
export function negotiateLocale(header: string | null | undefined): Locale | null {
  if (!header) return null;

  const ranked = header
    .split(',')
    .map((part, index) => {
      const [tag = '', ...params] = part.trim().split(';');
      const q = params
        .map((param) => param.trim())
        .find((param) => param.startsWith('q='));
      const weight = q ? Number.parseFloat(q.slice(2)) : 1;
      return { tag, weight: Number.isFinite(weight) ? weight : 0, index };
    })
    .filter((entry) => entry.tag !== '' && entry.tag !== '*' && entry.weight > 0)
    .sort((a, b) => b.weight - a.weight || a.index - b.index);

  for (const { tag } of ranked) {
    const primary = tag.toLowerCase().split('-')[0] ?? '';
    if (isLocale(primary)) return primary;
  }
  return null;
}

// ═══════════════════════════════════════════════════════════════════════════
// Koddaki iki dilli metinler — `{ tr, en }`
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Katalog verisinin (sahne şablonları, video şablonları, beceri adları,
 * geliştirici API açıklamaları…) iki dilli metni.
 *
 * Diğer on bir dil bu nesnelere YAZILMIYOR — her şablona on bir alan eklemek
 * kaynak dosyaları okunmaz hâle getirirdi. Çeviriler İngilizce metnin
 * özetiyle (`hashText`) anahtarlanan bir sözlükte duruyor; web'de o sözlük
 * `messages/<dil>.json` → `dataText` ad alanından geliyor.
 */
export interface LocalizedText {
  tr: string;
  en: string;
}

/**
 * İngilizce kaynak metnin kısa, kararlı özeti (FNV-1a 32 bit, onaltılık).
 *
 * Sözlük anahtarı bu, metnin kendisi değil: next-intl anahtardaki noktayı
 * iç içe yol sayıyor ve cümleler nokta taşıyor. İngilizce metin değişince
 * özet de değişiyor — eski çeviri kendiliğinden eskimiş sayılıyor ve betik
 * yeniden çeviriyor, kimse "bu çeviri güncel mi" diye bakmak zorunda değil.
 */
export function hashText(text: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return `t${(hash >>> 0).toString(16).padStart(8, '0')}`;
}

/**
 * `{ tr, en }` nesnesinden istenen dilin metni.
 *
 * `dictionary` çeviri sözlüğü (`hashText(en)` → metin). Yoksa ya da metin
 * henüz çevrilmemişse İngilizce döner.
 */
export function localizedText(
  text: LocalizedText,
  locale: string,
  dictionary?: Readonly<Record<string, string>> | null,
): string {
  if (locale === 'tr') return text.tr;
  if (locale === 'en') return text.en;
  return dictionary?.[hashText(text.en)] ?? text.en;
}

/**
 * Metin OLMAYAN alanlar — derin çeviride yaprakları dokunulmadan geçiyor.
 *
 * Yardım makalesi bloklarında `type: 'note'`, `to: '/pricing'`, `slug` gibi
 * alanlar var; bunlar ekranda görünmüyor, kodun karar verdiği değerler. Aynı
 * dize başka bir yerde METİN olarak çevrilmişse (sözlük özetle çalışıyor)
 * `type` bozulur ve blok hiç çizilmezdi. Çeviri betiği aynı listeyi
 * okuyup bu alanları sözlüğe hiç almıyor.
 */
export const NON_TEXT_KEYS: ReadonlySet<string> = new Set([
  'type',
  'to',
  'href',
  'slug',
  'route',
  'id',
  'key',
  'kind',
  'icon',
  'category',
  'value',
  'code',
  'lang',
  'locale',
]);

function translateLeaves(value: unknown, dictionary: Readonly<Record<string, string>>): unknown {
  if (typeof value === 'string') return dictionary[hashText(value)] ?? value;
  if (Array.isArray(value)) return value.map((item) => translateLeaves(item, dictionary));
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, entry]) => [
        key,
        NON_TEXT_KEYS.has(key) ? entry : translateLeaves(entry, dictionary),
      ]),
    );
  }
  return value;
}

/**
 * `{ tr: T, en: T }` — iki dilli bir NESNE ağacından (yardım makalesi gibi)
 * istenen dilin ağacı. Diğer dillerde İngilizce ağacın her metin yaprağı
 * sözlükten çevriliyor; çevirisi olmayan yaprak İngilizce kalıyor.
 */
export function localizedDeep<T>(
  pair: { tr: T; en: T },
  locale: string,
  dictionary?: Readonly<Record<string, string>> | null,
): T {
  if (locale === 'tr') return pair.tr;
  if (locale === 'en' || !dictionary) return pair.en;
  return translateLeaves(pair.en, dictionary) as T;
}
